// File: Home.js
//
// The Home page — the main dashboard after a user signs in. This is
// the most important and most-edited page in the app. It's also the
// VISUAL TEMPLATE other pages copy (header style, card style, etc.),
// so be careful changing layout/styling here.
//
// What's on this page:
//   - Sticky header with the logo + a Logout button
//   - Welcome card with the user's first name
//   - On mobile: a "Your Messages" preview (recent threads)
//   - "Shortcut" tiles: Inquiry / Calendar / Resources / Settings
//     (admins also see "Staff Portal")
//   - On desktop: a sidebar with the Account card + Messages preview
//   - On mobile: an Account dock pinned to the bottom of the screen
//   - A "?" Guide button (bottom-right) that opens the tutorial
//   - First-time-user tutorial overlay (dark mask + step popup)
//   - Logout confirmation popup
//
// Sections in this file (in the order they appear below):
//   - Constants (storage key for the tutorial)
//   - Icons             (small SVG icons used by the shortcut tiles)
//   - Helper components (AvatarPlaceholder, FieldRow, ShortcutCard)
//   - Home              (the actual page: data loading, tutorial logic,
//                        and the rendered layout)
//
// Common things you might want to change:
//   - Add/remove a shortcut tile -> look for the `links` useMemo below
//   - Edit the welcome guide steps -> look for the `tutorialSteps`
//     useMemo below
//   - Change the account fields shown -> look for <FieldRow .../>
//   - Reset the tutorial so it shows again -> change
//     HOME_TUTORIAL_STORAGE_KEY's value (bumping the "v1" works)

import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
  useLayoutEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "./firebaseConfig";
import { signOut } from "firebase/auth";
import {
  doc,
  getDoc,
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  limit,
} from "firebase/firestore";

import logo from "./assets/512.png";
import { COLORS, THEME, pageBackground } from "./theme";
import ThreadRow from "./components/ThreadRow";
import LogoutConfirm from "./components/LogoutConfirm";

const db = getFirestore();

// Key used to remember (in the browser) that the user has already
// seen the welcome tutorial. Bumping the version (e.g. v1 -> v2)
// effectively re-shows the tutorial to everyone.
const HOME_TUTORIAL_STORAGE_KEY = "home_tutorial_seen_v1";

// --- Icons ---
// One <svg> component per icon used on the shortcut tiles below.
// Each takes a `className` so the size can be tweaked at the call site.
function IconInquiry({ className = "w-7 h-7" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M8 10h8M8 14h5" />
      <path d="M6 3h12a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V5a2 2 0 012-2z" />
    </svg>
  );
}

function IconResources({ className = "w-6 h-6" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 18v-6" />
      <path d="M12 8h.01" />
      <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function IconCalendar({ className = "w-6 h-6" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M8 3v2M16 3v2" />
      <path d="M4 8h16" />
      <path d="M6 5h12a2 2 0 012 2v13a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2z" />
    </svg>
  );
}

function IconSettings({ className = "w-6 h-6" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 6h10" />
      <path d="M18 6h2" />
      <circle cx="16" cy="6" r="2" />
      <path d="M4 12h2" />
      <path d="M10 12h10" />
      <circle cx="8" cy="12" r="2" />
      <path d="M4 18h10" />
      <path d="M18 18h2" />
      <circle cx="16" cy="18" r="2" />
    </svg>
  );
}

function IconStaffPortal({ className = "w-6 h-6" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 2l7 4v6c0 5-3 9-7 10-4-1-7-5-7-10V6l7-4z" />
      <path d="M9 12l2 2 4-5" />
    </svg>
  );
}

// Fallback "person" silhouette shown when a user has no profile
// photo (or their photo URL fails to load).
function AvatarPlaceholder({ className = "w-10 h-10" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="10" r="3" />
      <path d="M7 19c1.2-2.6 3.4-4 5-4s3.8 1.4 5 4" />
    </svg>
  );
}

// Quick sanity check that a profile photo URL is something we'd
// actually try to render (http/https/data/blob), not a garbled value.
function isLikelyImageUrl(url) {
  if (!url || typeof url !== "string") return false;
  const u = url.trim();
  if (u.startsWith("http://") || u.startsWith("https://")) return true;
  if (u.startsWith("data:image/")) return true;
  if (u.startsWith("blob:")) return true;
  return false;
}

// One "label on the left, value on the right" row inside the Account
// card (e.g. Cell: (555) 123-4567). Used inside the sidebar/dock.
function FieldRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div
        className="text-xs font-semibold"
        style={{ color: "rgba(31,56,0,0.55)" }}
      >
        {label}
      </div>
      <div
        className="text-sm text-right break-all"
        style={{ color: "rgba(31,56,0,0.76)" }}
      >
        {value || "—"}
      </div>
    </div>
  );
}

// One of the big clickable tiles on the home dashboard (Inquiry,
// Calendar, Resources, Settings, Staff Portal). Renders an icon, a
// title, a description, and an optional badge. The `tutorialActive`
// prop adds a glowing ring when the welcome tutorial is highlighting
// this tile.
const ShortcutCard = React.forwardRef(function ShortcutCard(
  {
    title,
    description,
    icon,
    onClick,
    disabled = false,
    badge,
    tutorialActive = false,
    accent = THEME.primary,
  },
  ref
) {
  return (
    <button
      ref={ref}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={[
        "text-left rounded-2xl border transition p-4 sm:p-5 backdrop-blur relative overflow-hidden",
        tutorialActive ? "z-[130] ring-4 ring-white/90 shadow-2xl" : "",
        disabled
          ? "opacity-60 cursor-not-allowed"
          : "hover:-translate-y-0.5 hover:shadow-lg active:scale-[0.99]",
      ].join(" ")}
      style={{
        backgroundColor: disabled ? "rgba(255,255,255,0.64)" : THEME.cardBg,
        borderColor: THEME.border,
        boxShadow: disabled ? "none" : "0 14px 28px rgba(31,56,0,0.1)",
      }}
    >
      {!disabled ? (
        <div
          className="absolute inset-x-0 top-0 h-1"
          style={{ backgroundColor: accent }}
        />
      ) : null}

      <div className="flex items-start gap-3">
        <div
          className="rounded-xl p-2 border"
          style={{
            color: disabled ? "rgba(31,56,0,0.4)" : accent,
            backgroundColor: "#ffffff",
            borderColor: THEME.border,
          }}
        >
          {icon}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div
              className="text-lg font-extrabold"
              style={{
                color: disabled ? "rgba(31,56,0,0.55)" : THEME.text,
              }}
            >
              {title}
            </div>

            {badge ? (
              <span
                className="text-[11px] font-extrabold px-2 py-0.5 rounded-full border"
                style={{
                  backgroundColor: COLORS.yellowLight,
                  borderColor: "rgba(61,51,0,0.18)",
                  color: COLORS.brown,
                }}
              >
                {badge}
              </span>
            ) : null}
          </div>

          <div
            className="text-sm mt-1"
            style={{
              color: disabled ? "rgba(31,56,0,0.45)" : "rgba(31,56,0,0.66)",
            }}
          >
            {description}
          </div>
        </div>
      </div>
    </button>
  );
});

function Home() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [cell, setCell] = useState("");

  const [isAdmin, setIsAdmin] = useState(false);

  const [photoURL, setPhotoURL] = useState("");
  const [avatarBroken, setAvatarBroken] = useState(false);

  const [threadsLoading, setThreadsLoading] = useState(true);
  const [threads, setThreads] = useState([]);

  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [highlightRect, setHighlightRect] = useState(null);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [isMobileLayout, setIsMobileLayout] = useState(
    typeof window !== "undefined" ? window.innerWidth < 1024 : false
  );

  // References to UI elements the tutorial points at. Each `ref` gets
  // attached to a specific element in the JSX below, then the tutorial
  // uses it to draw a glowing highlight around that element.
  const logoutRef = useRef(null);
  const inquiryRef = useRef(null);
  const resourcesRef = useRef(null);
  const calendarRef = useRef(null);
  const settingsRef = useRef(null);
  const messagesDesktopRef = useRef(null);
  const messagesMobileRef = useRef(null);
  const accountDesktopRef = useRef(null);
  const accountMobileRef = useRef(null);
  const staffPortalRef = useRef(null);

  // Re-check whether the window is phone-sized whenever it gets
  // resized. Used to decide where the tutorial should point on each
  // step (mobile vs desktop layouts hold the same controls in
  // different places).
  useEffect(() => {
    const handleResize = () => {
      setIsMobileLayout(window.innerWidth < 1024);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch the signed-in user's profile (firstName, photoURL, isAdmin,
  // etc.) and their recent message threads from Firestore. Runs on
  // mount and again whenever the signed-in user changes. If they are
  // not signed in, we bounce them back to the login page.
  useEffect(() => {
    let isMounted = true;

    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate("/", { replace: true });
        return;
      }

      try {
        setEmail(user.email || "");

        const userRef = doc(db, "users", user.uid);
        const snap = await getDoc(userRef);

        if (!snap.exists()) {
          navigate("/sign-up", { replace: true });
          return;
        }

        const data = snap.data() || {};
        if (!isMounted) return;

        const fn = data.firstName || (data.name ? data.name.split(" ")[0] : "");
        const ln =
          data.lastName ||
          (data.name && data.name.split(" ").length > 1
            ? data.name.split(" ").slice(1).join(" ")
            : "");

        setFirstName(fn);
        setLastName(ln);
        setCell(data.cell || data.phone || data.phoneNumber || "");
        setIsAdmin(data.staff === true);

        const candidate =
          (isLikelyImageUrl(data.photoURL) && data.photoURL) ||
          (isLikelyImageUrl(data.profilePhoto) && data.profilePhoto) ||
          (isLikelyImageUrl(data.profilePic) && data.profilePic) ||
          (isLikelyImageUrl(user.photoURL) && user.photoURL) ||
          "";

        setAvatarBroken(false);
        setPhotoURL(candidate);

        setThreadsLoading(true);

        const threadsRef = collection(db, "threads");
        const q = query(
          threadsRef,
          where("participants", "array-contains", user.uid),
          limit(6)
        );

        const snapThreads = await getDocs(q);

        const list = snapThreads.docs.map((d) => ({
          id: d.id,
          ...(d.data() || {}),
        }));

        list.sort((a, b) => {
          const ta = a.updatedAt?.toMillis?.() ?? 0;
          const tb = b.updatedAt?.toMillis?.() ?? 0;
          return tb - ta;
        });

        setThreads(list);
      } catch (e) {
        console.error("Home: failed to load data", e);
      } finally {
        if (isMounted) {
          setLoading(false);
          setThreadsLoading(false);
        }
      }
    });

    return () => {
      isMounted = false;
      unsub();
    };
  }, [navigate]);

  useEffect(() => {
    if (loading) return;

    const alreadySeen =
      localStorage.getItem(HOME_TUTORIAL_STORAGE_KEY) === "true";
    if (!alreadySeen) {
      const t = setTimeout(() => {
        setTutorialStep(0);
        setTutorialOpen(true);
      }, 250);
      return () => clearTimeout(t);
    }
  }, [loading]);

  // Signs the user out of Firebase and sends them back to the login
  // page. Runs after they click "Log out" in the confirmation popup.
  const handleLogout = useCallback(async () => {
    try {
      setLoggingOut(true);
      await signOut(auth);
      localStorage.removeItem("userEmail");
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout Failed:", error);
      alert("Logout failed. Please try again.");
      setLoggingOut(false);
      setLogoutConfirmOpen(false);
    }
  }, [navigate]);

  // =========================================================
  // The shortcut tiles on the home dashboard.
  // To ADD a new tile: copy one of the entries inside `orderedLinks`
  // and adjust title / description / icon / to (URL).
  // To REMOVE a tile: delete its entry.
  // To REORDER tiles: rearrange the entries.
  // To change which tiles ADMINS see: see the `if (isAdmin)` block.
  // =========================================================
  const links = useMemo(() => {
    const orderedLinks = [
      {
        key: "inquiry",
        title: "Inquiry",
        description: "Ask a question or request help.",
        icon: <IconInquiry />,
        to: "/inquiry",
        accent: COLORS.yellowLight,
      },
      {
        key: "calendar",
        title: "Calendar",
        description: "View upcoming dates and events.",
        icon: <IconCalendar />,
        externalUrl: "https://increasinghope.org/events/",
        accent: COLORS.yellowDark,
      },
      {
        key: "resources",
        title: "Resources",
        description: "Guides, links, and helpful info.",
        icon: <IconResources />,
        to: "/resources",
        accent: COLORS.aquaBase,
      },
    ];

    if (isAdmin) {
      orderedLinks.push({
        key: "staff-portal",
        title: "Staff Portal",
        description: "Admin tools and staff access.",
        icon: <IconStaffPortal />,
        to: "/admin",
        accent: COLORS.aquaDark,
      });
    }

    orderedLinks.push({
      key: "settings",
      title: "Settings",
      description: "Update preferences and account options.",
      icon: <IconSettings />,
      to: "/usersettings",
      disabled: false,
      accent: COLORS.greenDark,
    });

    return orderedLinks;
  }, [isAdmin]);

  // =========================================================
  // The steps shown in the first-time-user welcome tutorial.
  // Each step highlights one part of the page (via `ref`) and shows
  // a title + body of text. To add a step, copy an entry; to remove
  // one, delete it.
  // =========================================================
  const tutorialSteps = useMemo(() => {
    const steps = [
      {
        key: "inquiry",
        title: "Inquiry",
        text: "Have a question, need support, or want to talk to us? Use the inquiry page, which will walk you through your request to us.",
        ref: inquiryRef,
      },
      {
        key: "calendar",
        title: "Calendar",
        text: "This is where upcoming events and important dates can be viewed.",
        ref: calendarRef,
      },
      {
        key: "resources",
        title: "Resources",
        text: "This is the main place to find SC211 and other local resources.",
        ref: resourcesRef,
      },
    ];

    if (isAdmin) {
      steps.push({
        key: "staff-portal",
        title: "Staff Portal",
        text: "As an administrator, you have access to the staff portal. Here, you can handle inquiries and staff tools.",
        ref: staffPortalRef,
      });
    }

    steps.push(
      {
        key: "settings",
        title: "Settings",
        text: "This is where you can update your account information and preferences.",
        ref: settingsRef,
      },
      {
        key: "messages",
        title: "Your Messages",
        text: "This section shows recent message threads so you can quickly jump back into your conversations.",
        ref: isMobileLayout ? messagesMobileRef : messagesDesktopRef,
      },
      {
        key: "account",
        title: "Account",
        text: "This shows your relevant account information that we have in our database.",
        ref: isMobileLayout ? accountMobileRef : accountDesktopRef,
      },
      {
        key: "logout",
        title: "Logout",
        text: "This signs you out of the web app.",
        ref: logoutRef,
      }
    );

    return steps;
  }, [isAdmin, isMobileLayout]);

  const activeTutorialStep = tutorialSteps[tutorialStep] || null;

  const updateHighlight = useCallback(() => {
    if (!tutorialOpen || !activeTutorialStep?.ref?.current) {
      setHighlightRect(null);
      return;
    }

    const el = activeTutorialStep.ref.current;
    el.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "center",
    });

    window.requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      setHighlightRect({
        top: Math.max(rect.top - 6, 8),
        left: Math.max(rect.left - 6, 8),
        width: rect.width + 12,
        height: rect.height + 12,
      });
    });
  }, [tutorialOpen, activeTutorialStep]);

  useLayoutEffect(() => {
    updateHighlight();
  }, [
    updateHighlight,
    tutorialStep,
    tutorialOpen,
    threadsLoading,
    isAdmin,
    isMobileLayout,
  ]);

  useEffect(() => {
    if (!tutorialOpen) return;

    const handleResize = () => updateHighlight();
    const handleScroll = () => updateHighlight();

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [tutorialOpen, updateHighlight]);

  useEffect(() => {
    if (!tutorialOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [tutorialOpen]);

  const closeTutorial = useCallback((markSeen = true) => {
    if (markSeen) {
      localStorage.setItem(HOME_TUTORIAL_STORAGE_KEY, "true");
    }
    setTutorialOpen(false);
    setHighlightRect(null);
  }, []);

  const reopenTutorial = useCallback(() => {
    setTutorialStep(0);
    setTutorialOpen(true);
  }, []);

  const nextTutorialStep = useCallback(() => {
    if (tutorialStep >= tutorialSteps.length - 1) {
      closeTutorial(true);
      return;
    }
    setTutorialStep((prev) => prev + 1);
  }, [tutorialStep, tutorialSteps.length, closeTutorial]);

  const prevTutorialStep = useCallback(() => {
    setTutorialStep((prev) => Math.max(prev - 1, 0));
  }, []);

  if (loading) {
    return (
      <div
        className="flex-1 flex items-center justify-center"
        style={pageBackground}
      >
        <div
          className="rounded-2xl border px-5 py-4 bg-white shadow-lg"
          style={{ borderColor: THEME.border }}
        >
          <div className="text-base font-extrabold" style={{ color: THEME.text }}>
            Loading…
          </div>
        </div>
      </div>
    );
  }

  const fullName =
    `${firstName || ""}${lastName ? ` ${lastName}` : ""}`.trim() || "User";
  const showImage = !!photoURL && !avatarBroken;

  const Avatar = ({ sizeClass }) => (
    <div
      className={`${sizeClass} rounded-2xl border bg-white overflow-hidden flex items-center justify-center`}
      style={{ borderColor: THEME.border }}
    >
      {showImage ? (
        <img
          src={photoURL}
          alt=""
          className="w-full h-full object-cover"
          onError={() => setAvatarBroken(true)}
          referrerPolicy="no-referrer"
        />
      ) : (
        <div style={{ color: THEME.primary }}>
          <AvatarPlaceholder className="w-9 h-9" />
        </div>
      )}
    </div>
  );

  const openThread = (t) => {
    navigate("/messages", { state: { threadId: t.id } });
  };

  const isStepActive = (key) => tutorialOpen && activeTutorialStep?.key === key;

  const getCardRef = (itemKey) => {
    switch (itemKey) {
      case "inquiry":
        return inquiryRef;
      case "calendar":
        return calendarRef;
      case "resources":
        return resourcesRef;
      case "staff-portal":
        return staffPortalRef;
      case "settings":
        return settingsRef;
      default:
        return null;
    }
  };

  return (
    <div className="flex-1 relative" style={pageBackground}>
      {/* --- Page header: logo on left, Logout button on right. The
              Logout button is wired to the LogoutConfirm popup
              defined near the bottom of this file. --- */}
      <header
        className="sticky top-0 z-20 backdrop-blur border-b shadow-[0_6px_18px_rgba(31,56,0,0.16)]"
        style={{
          backgroundColor: THEME.headerBg,
          borderColor: "rgba(231,247,210,0.28)",
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 sm:py-3 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate("/home")}
            className="flex items-center gap-3 rounded-xl px-2 py-1 hover:bg-white/10 "
              
            aria-label="Go to home"
          >
            <img
              src={logo}
              alt="Logo"
              className="w-13 h-12 object-contain"
            />

            <div className="leading-tight text-left">
              <div className="text-sm font-extrabold text-white">
                H.O.P.E Navigator
              </div>
              <div
                className="text-[12px] font-semibold"
                style={{ color: COLORS.yellowBase }}
              >
                Home
              </div>
            </div>
          </button>

          <button
            ref={logoutRef}
            onClick={() => setLogoutConfirmOpen(true)}
            className={[
              "text-sm font-extrabold text-white/90 hover:text-white transition rounded-xl px-3 py-2 hover:bg-white/10 relative",
              isStepActive("logout")
                ? "z-[130] ring-4 ring-white/90 shadow-2xl"
                : "",
            ].join(" ")}
          >
            Logout
          </button>
        </div>
      </header>

      {/* --- Main page body. Two-column layout on desktop: the big
              welcome card on the left and the Account/Messages
              sidebar on the right. On phones it stacks vertically. --- */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6 lg:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-4 lg:gap-6">
          <section
            className="backdrop-blur border rounded-3xl p-3 sm:p-4 lg:p-5"
            style={{
              backgroundColor: THEME.cardBg,
              borderColor: THEME.border,
              boxShadow: THEME.shadow,
            }}
          >
            <div
              className="inline-flex items-center rounded-full px-3 py-1 text-sm font-extrabold"
              style={{}}
            >
              Welcome
            </div>

            <h1
              className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mt-3"
              style={{ color: THEME.text }}
            >
              {firstName || "User"}
            </h1>

            <p className="mt-2 max-w-2xl text-sm sm:text-base" style={{ color: "rgba(31,56,0,0.66)" }}>
              Choose where you want to go next.
            </p>

            <div
              ref={messagesMobileRef}
              className={[
                "lg:hidden mt-6 relative rounded-2xl border p-4",
                isStepActive("messages")
                  ? "z-[130] ring-4 ring-white/90 shadow-2xl"
                  : "",
              ].join(" ")}
              style={{
                backgroundColor: THEME.cardBgSoft,
                borderColor: THEME.border,
              }}
            >
              <div className="flex items-center justify-between">
                <div
                  className="text-sm font-extrabold"
                  style={{ color: THEME.text }}
                >
                  Your Messages
                </div>

                <button
                  onClick={() => navigate("/messages")}
                  className="text-[12px] font-extrabold underline decoration-black/20 hover:decoration-black/40"
                  style={{ color: COLORS.aquaDark }}
                >
                  View all
                </button>
              </div>

              <div className="mt-3 space-y-2">
                {threadsLoading ? (
                  <div className="text-sm" style={{ color: "rgba(31,56,0,0.62)" }}>
                    Loading threads…
                  </div>
                ) : threads.length === 0 ? (
                  <div className="text-sm" style={{ color: "rgba(31,56,0,0.62)" }}>
                    No threads yet.
                  </div>
                ) : (
                  threads.slice(0, 3).map((t) => (
                    <ThreadRow
                      key={t.id}
                      thread={t}
                      onClick={() => openThread(t)}
                    />
                  ))
                )}
              </div>
            </div>

            <div className="mt-4 sm:mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
              {links.map((item) => (
                <ShortcutCard
                  key={item.key}
                  ref={getCardRef(item.key)}
                  title={item.title}
                  description={item.description}
                  icon={item.icon}
                  badge={item.badge}
                  disabled={item.disabled}
                  accent={item.accent}
                  tutorialActive={isStepActive(item.key)}
                  onClick={() => {
                    if (item.externalUrl) {
                      window.open(item.externalUrl, "_blank", "noopener,noreferrer");
                      return;
                    }
                    navigate(item.to);
                  }}
                />
              ))}
            </div>
          </section>

          {/* --- Desktop sidebar (right column). Contains the Account
                  card and the "Your Messages" recent threads card.
                  Hidden on phones, where the same info is shown in a
                  bottom dock and inside the welcome card. --- */}
          <aside className="hidden lg:block">
            <div className="sticky top-[64px] sm:top-[72px] space-y-3 sm:space-y-4">
              <div
                ref={accountDesktopRef}
                className={[
                  "rounded-2xl border p-4 sm:p-5 backdrop-blur relative",
                  isStepActive("account")
                    ? "z-[130] ring-4 ring-white/90 shadow-2xl"
                    : "",
                ].join(" ")}
                style={{
                  backgroundColor: THEME.cardBg,
                  borderColor: THEME.border,
                  boxShadow: "0 14px 30px rgba(31,56,0,0.1)",
                }}
              >
                <div
                  className="text-sm font-extrabold"
                  style={{ color: THEME.text }}
                >
                  Account
                </div>

                <div className="mt-3 flex items-center gap-3">
                  <Avatar sizeClass="w-10 h-10" />

                  <div className="min-w-0">
                    <div
                      className="text-base font-extrabold truncate"
                      style={{ color: THEME.text }}
                    >
                      {fullName}
                    </div>
                    <div
                      className="text-sm break-all"
                      style={{ color: "rgba(31,56,0,0.62)" }}
                    >
                      {email || "—"}
                    </div>
                  </div>
                </div>

                <div
                  className="mt-3 space-y-2 rounded-xl border p-3"
                  style={{
                    borderColor: THEME.border,
                  }}
                >
                  <FieldRow label="Cell" value={cell} />
                </div>
              </div>

              <div
                ref={messagesDesktopRef}
                className={[
                  "rounded-2xl border p-4 sm:p-5 backdrop-blur relative",
                  isStepActive("messages")
                    ? "z-[130] ring-4 ring-white/90 shadow-2xl"
                    : "",
                ].join(" ")}
                style={{
                  backgroundColor: THEME.cardBg,
                  borderColor: THEME.border,
                  boxShadow: "0 14px 30px rgba(31,56,0,0.1)",
                }}
              >
                <div className="flex items-center justify-between gap-3">
                  <div
                    className="text-sm font-extrabold"
                    style={{ color: THEME.text }}
                  >
                    Your Messages
                  </div>

                  <button
                    onClick={() => navigate("/messages")}
                    className="text-[12px] font-extrabold underline decoration-black/20 hover:decoration-black/40"
                    style={{ color: COLORS.aquaDark }}
                  >
                    View all
                  </button>
                </div>

                <div className="mt-3 space-y-2 max-h-[35vh] sm:max-h-[42vh] overflow-y-auto pr-1">
                  {threadsLoading ? (
                    <div className="text-sm" style={{ color: "rgba(31,56,0,0.62)" }}>
                      Loading threads…
                    </div>
                  ) : threads.length === 0 ? (
                    <div className="text-sm" style={{ color: "rgba(31,56,0,0.62)" }}>
                      No threads yet.
                    </div>
                  ) : (
                    threads.map((t) => (
                      <ThreadRow
                        key={t.id}
                        thread={t}
                        onClick={() => openThread(t)}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </main>

      {/* --- Mobile bottom dock. Pinned to the bottom of the screen on
              phones (hidden on desktop). Shows the user's name, photo,
              and key contact info. Same content as the desktop
              Account card, just laid out compactly for one-thumb use. --- */}
      <div className="lg:hidden fixed left-0 right-0 bottom-0 z-30 px-4 pb-[calc(env(safe-area-inset-bottom,0px)+12px)] pt-3">
        <div className="max-w-6xl mx-auto">
          <div
            ref={accountMobileRef}
            className={[
              "rounded-2xl border shadow-lg backdrop-blur px-4 py-3 relative",
              isStepActive("account")
                ? "z-[130] ring-4 ring-white/90 shadow-2xl"
                : "",
            ].join(" ")}
            style={{
              backgroundColor: "rgba(255,255,255,0.96)",
              borderColor: THEME.border,
              boxShadow: "0 14px 28px rgba(31,56,0,0.16)",
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-11 h-11 rounded-2xl border bg-white overflow-hidden flex items-center justify-center"
                style={{ borderColor: THEME.border }}
              >
                {showImage ? (
                  <img
                    src={photoURL}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={() => setAvatarBroken(true)}
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div style={{ color: THEME.primary }}>
                    <AvatarPlaceholder className="w-8 h-8" />
                  </div>
                )}
              </div>

              <div className="min-w-0 flex-1">
                <div
                  className="text-sm font-extrabold truncate"
                  style={{ color: THEME.text }}
                >
                  {fullName}
                </div>
                <div
                  className="text-[12px] truncate"
                  style={{ color: "rgba(31,56,0,0.58)" }}
                >
                  {email || "—"}
                </div>
              </div>
            </div>

            <div
              className="mt-3 space-y-2 rounded-xl border px-3 py-2"
              style={{
                backgroundColor: COLORS.greenLight,
                borderColor: THEME.border,
              }}
            >
              <FieldRow label="Cell" value={cell} />
            </div>
          </div>
        </div>
      </div>

      {/* Spacer so the mobile dock doesn't cover the bottom of the
          scrollable content on phones. */}
      <div className="lg:hidden h-44" />

      {/* --- Floating "Guide" button (bottom-right). Click reopens
              the welcome tutorial at step 1. --- */}
      <button
        type="button"
        onClick={reopenTutorial}
        className="fixed bottom-5 right-5 z-40 rounded-full shadow-lg px-4 py-3 text-sm font-extrabold border transition hover:-translate-y-0.5"
        style={{
          color: COLORS.brown,
          backgroundColor: COLORS.yellowBase,
          borderColor: "rgba(61,51,0,0.2)",
          boxShadow: "0 12px 24px rgba(61,51,0,0.18)",
        }}
        aria-label="Open getting started guide"
      >
        Guide
      </button>

      {/* --- Welcome tutorial overlay. When `tutorialOpen` is true,
              a dark mask covers the page and a highlight rectangle is
              drawn around the current step's target element. The small
              popup at the bottom-right has the step's text and the
              Back/Next/Done buttons. To edit the tutorial steps, look
              for the `tutorialSteps` useMemo near the top of this
              component. --- */}
      {tutorialOpen && (
        <>
          <div className="fixed inset-0 z-[120] bg-black/55" />

          {highlightRect ? (
            <div
              className="fixed z-[125] rounded-3xl border-2 border-white pointer-events-none transition-all duration-200"
              style={{
                top: highlightRect.top,
                left: highlightRect.left,
                width: highlightRect.width,
                height: highlightRect.height,
                boxShadow:
                  "0 0 0 9999px rgba(0,0,0,0.6), 0 0 0 4px rgba(255,255,255,1), 0 0 28px rgba(153,242,255,0.7), 0 10px 34px rgba(0,0,0,0.38)",
              }}
            />
          ) : null}

          <div className="fixed inset-x-4 bottom-4 sm:inset-x-auto sm:right-6 sm:bottom-6 z-[140] sm:w-[380px]">
            <div
              className="rounded-3xl border shadow-2xl bg-white p-5"
              style={{ borderColor: THEME.border }}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div
                    className="text-xs font-extrabold uppercase tracking-wide"
                    style={{ color: COLORS.aquaDark }}
                  >
                    Getting started
                  </div>
                  <div
                    className="text-lg font-extrabold mt-1"
                    style={{ color: THEME.text }}
                  >
                    {activeTutorialStep?.title || "Guide"}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => closeTutorial(true)}
                  className="text-sm font-bold transition"
                  style={{ color: "rgba(31,56,0,0.55)" }}
                  aria-label="Close guide"
                >
                  Skip
                </button>
              </div>

              <p
                className="mt-3 text-sm leading-6"
                style={{ color: "rgba(31,56,0,0.72)" }}
              >
                {activeTutorialStep?.text}
              </p>

              <div className="mt-4 flex items-center justify-between gap-3">
                <div
                  className="text-xs font-semibold"
                  style={{ color: "rgba(31,56,0,0.52)" }}
                >
                  Step {tutorialStep + 1} of {tutorialSteps.length}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={prevTutorialStep}
                    disabled={tutorialStep === 0}
                    className="px-3 py-2 rounded-xl border text-sm font-bold bg-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-black/5 transition"
                    style={{
                      color: "rgba(31,56,0,0.72)",
                      borderColor: THEME.border,
                    }}
                  >
                    Back
                  </button>

                  <button
                    type="button"
                    onClick={nextTutorialStep}
                    className="px-4 py-2 rounded-xl text-sm font-extrabold text-white transition hover:opacity-90"
                    style={{
                      backgroundColor: THEME.primary,
                      boxShadow: "0 10px 20px rgba(64,115,0,0.18)",
                    }}
                  >
                    {tutorialStep === tutorialSteps.length - 1 ? "Done" : "Next"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <LogoutConfirm
        open={logoutConfirmOpen}
        busy={loggingOut}
        onCancel={() => setLogoutConfirmOpen(false)}
        onConfirm={handleLogout}
      />
    </div>
  );
}

export default Home;